import { CircleHelp } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Label } from '@/components/inputs';
import { Toggle } from '@/components/inputs/toggle';
import { Share } from '@/components/modules/share';
import { Tooltip } from '@/components/tooltip';
import { useShareItem } from '@/hooks/use-share-item';
import { SHARED_ITEM_TYPE } from '@/lib/constants';
import { PortalContainer } from '@/lib/context/portal-container';
import { logEvent } from '@/utils/logger';
import { tv } from '@/utils/styles';

export interface ShipmentTabsShareProps {
  savedItemId: string;
}

const ShipmentTabsShare = ({ savedItemId }: ShipmentTabsShareProps) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const { sharedItem, updateSharedItem, isLoading, error, createSharedItem } = useShareItem({
    type: SHARED_ITEM_TYPE.SHIPMENT_VIEW,
    isEnabled: true,
    itemId: savedItemId,
    shouldCreate: false,
  });

  const handleEnableChange = async (value: boolean) => {
    if (value && !sharedItem && !isCreating) {
      setIsCreating(true);
      try {
        await createSharedItem();
      } catch (e: any) {
        logEvent.error('Error creating shared item', e);
      } finally {
        setIsCreating(false);
      }
    }

    if (sharedItem) {
      updateSharedItem({ isEnabled: value });
    }

    setIsEnabled(value);
  };

  useEffect(() => {
    if (sharedItem) {
      // Automatically toggle isEnabled if there is an existing shared item
      // record
      setIsEnabled(sharedItem.isEnabled);
    }
  }, [sharedItem]);

  const { base } = styles();

  return (
    <PortalContainer>
      <div className={base()}>
        <div className="flex">
          <Toggle name="share" legend="Share" on={isEnabled} onChange={handleEnableChange} />
          <div className="flex items-center gap-1">
            <Label className="!m-0" htmlFor="share">
              Share view
            </Label>
            <Tooltip content="Allows you to share this view with others via a link">
              <CircleHelp size={12} />
            </Tooltip>
          </div>
        </div>
        {isEnabled ? (
          <Share sharedItem={sharedItem} error={error} isLoading={isLoading} onUpdate={updateSharedItem} />
        ) : null}
      </div>
    </PortalContainer>
  );
};

const styles = tv({
  slots: {
    base: 'space-y-4',
  },
});

export { ShipmentTabsShare };
